import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Spacer,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Blocker } from "react-router-dom";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import {
  createFormSubmitEvent,
  FormSubmitEventType,
} from "shared/components/form/FormSubmitEvent";
import { ConfirmationDialogProps } from "shared/components/modal/confirm/ConfirmationDialog";

export const FormNavConfirmationDialog: React.FC<{
  blocker: Blocker;
  objectDisplayName?: string;
  onSubmit: (event: FormSubmitEventType) => Promise<boolean>;
  isSubmitting?: boolean;
}> = ({ blocker, objectDisplayName, onSubmit, isSubmitting }) => {
  const form = useFormContext();
  const onClickSubmit = useCallback(async () => {
    try {
      const wasSubmitSuccess = await onSubmit(
        createFormSubmitEvent({ skipOnSuccess: true }),
      );
      // Validation errors are not thrown
      const isValid = await form.trigger();
      if (isValid && wasSubmitSuccess) {
        blocker.proceed?.();
        return;
      }
    } catch (_) {
      // ignore
    }
    blocker.reset?.();
  }, [blocker, form, onSubmit]);
  const onClickCancel = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);
  return (
    <NavConfirmationDialog
      isOpen={blocker.state === "blocked"}
      objectDisplayName={objectDisplayName}
      isSubmitting={isSubmitting}
      onClickSubmit={onClickSubmit}
      onClickCancel={onClickCancel}
      onClickDiscard={blocker.proceed}
      onClose={onClickCancel}
    />
  );
};

export interface NavConfirmationDialogProps {
  isOpen: boolean;
  objectDisplayName?: string;
  isSubmitting?: boolean;
  onClickSubmit: () => Promise<void> | void;
  onClickCancel: (() => void) | undefined;
  onClickDiscard: (() => void) | undefined;
  onClose: () => void;
  onCloseComplete?: () => void;
}

export const NavConfirmationDialog: React.FC<NavConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onCloseComplete,
  objectDisplayName,
  isSubmitting,
  onClickSubmit,
  onClickCancel,
  onClickDiscard,
}) => {
  const { t } = useTranslation(["common"]);
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader>{t("common:unsavedProgress")}</AlertDialogHeader>
        <AlertDialogCloseButton onClick={onClickCancel} />
        <AlertDialogBody>
          <Trans
            ns="common"
            i18nKey={
              objectDisplayName
                ? "discardChangesMessageObject"
                : "discardChangesMessage"
            }
            values={{ objectDisplayName }}
          >
            Will lose changes to
            <Text as="span" fontWeight="semibold">
              object display name
            </Text>
            if you leave
          </Trans>
        </AlertDialogBody>
        <AlertDialogFooter gap={2}>
          <Button
            onClick={onClickDiscard}
            colorScheme="destructive"
            isDisabled={isSubmitting}
          >
            {t("common:discardChanges")}
          </Button>
          <Spacer />
          <ButtonOrder
            dismiss={
              <Button
                ref={cancelRef}
                onClick={onClickCancel}
                colorScheme="secondary"
                isDisabled={isSubmitting}
              >
                {t("common:cancel")}
              </Button>
            }
            confirm={
              <Button onClick={onClickSubmit} isLoading={isSubmitting}>
                {t("common:saveProgress")}
              </Button>
            }
          />
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const NavConfirmationConfirmDialog: React.FC<
  ConfirmationDialogProps<"submit" | "discard">
> = ({ alertProps, onSuccess, onCancel }) => (
  <NavConfirmationDialog
    {...alertProps}
    onClickSubmit={() => onSuccess("submit")}
    onClickCancel={onCancel}
    onClickDiscard={() => {
      onSuccess("discard");
    }}
  />
);
